export const APPOINTMENT_TYPES = [
  'consultation',
  'follow-up',
  'emergency',
  'checkup',
] as const;

export type AppointmentType = typeof APPOINTMENT_TYPES[number];
export const PATIENT_STATUSES = ['active', 'inactive', 'discharged'] as const;
export type PatientStatus = typeof PATIENT_STATUSES[number];

export function isAppointmentType(value: string): value is AppointmentType {
  return APPOINTMENT_TYPES.some(type => type === value);
}

export function isPatientStatus(value: string): value is PatientStatus {
  return PATIENT_STATUSES.some(status => status === value);
}

export interface PatientTag {
  id: number;
  name: string;
  color: string | null;
  folder_id: number | null;
}

export interface Patient {
  id: number;
  full_name: string;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  gender: string | null;
  blood_type: string | null;
  allergies: string | null;
  chronic_conditions: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  insurance_provider: string | null;
  insurance_policy_number: string | null;
  address: string | null;
  status: PatientStatus;
  notes: string | null;
  avatar_url: string | null;
  registration_date: string;
  created_at: string;
  updated_at: string | null;
  last_appointment_date: string | null;
  next_appointment_date: string | null;
  appointment_count: number;
  tags: PatientTag[];
}

export interface PatientListResponse {
  patients: Patient[];
  total: number;
  page: number;
  per_page: number;
}

export interface DoctorWorkday {
  id: number;
  doctor_id: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
  created_at: string;
  updated_at: string | null;
}

export interface DoctorVacation {
  id: number;
  doctor_id: number;
  start_date: string;
  end_date: string;
  reason: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface Doctor {
  id: number;
  name: string;
  specialty: string | null;
  phone: string | null;
  email: string | null;
  bio: string | null;
  color: string | null;
  is_active: boolean;
  workdays: DoctorWorkday[];
  vacations: DoctorVacation[];
  created_at: string;
  updated_at: string | null;
}

export interface DoctorPayload {
  name: string;
  specialty?: string | null;
  phone?: string | null;
  email?: string | null;
  bio?: string | null;
  color?: string | null;
  is_active?: boolean;
  workdays?: Array<Pick<DoctorWorkday, 'day_of_week' | 'start_time' | 'end_time'>>;
  vacations?: Array<Pick<DoctorVacation, 'start_date' | 'end_date' | 'reason'>>;
}

export interface DoctorListResponse {
  doctors: Doctor[];
  total: number;
  page: number;
  per_page: number;
}

export interface AppointmentDetails {
  chief_complaint: string | null;
  symptoms: string[] | null;
  diagnosis: string | null;
  treatment_plan: string | null;
  prescriptions: string | null;
  vitals_blood_pressure: string | null;
  vitals_heart_rate: number | null;
  vitals_temperature: number | null;
  vitals_weight: number | null;
  vitals_height: number | null;
  notes: string | null;
  follow_up_date: string | null;
}

export interface Appointment {
  id: number;
  patient_id: number;
  room_id: string;
  start_time: string;
  end_time: string;
  appointment_type: AppointmentType;
  status: string;
  doctor_id: number | null;
  doctor_name: string | null;
  details: AppointmentDetails | null;
  created_at: string;
  updated_at: string | null;
  patient_name: string | null;
  tags: PatientTag[];
}

/** Exact FastAPI response, including its computed compatibility alias. */
export interface AppointmentApiResponse extends Appointment {
  appointment_date: string;
}

export interface AppointmentListApiResponse {
  appointments: AppointmentApiResponse[];
  total: number;
  page: number;
  per_page: number;
}

export interface AppointmentListResponse {
  appointments: Appointment[];
  total: number;
  page: number;
  per_page: number;
}

export type AppointmentDetailsPayload = Partial<AppointmentDetails>;

export interface AppointmentCreatePayload {
  patient_id: number;
  room_id: string;
  start_time: string;
  end_time: string;
  appointment_type: AppointmentType;
  status?: string;
  doctor_id?: number | null;
  doctor_name?: string | null;
  details?: AppointmentDetailsPayload | null;
  tag_ids?: number[];
}

export interface AppointmentUpdatePayload {
  room_id?: string;
  start_time?: string;
  end_time?: string;
  appointment_type?: AppointmentType;
  status?: string;
  doctor_id?: number | null;
  doctor_name?: string | null;
  details?: AppointmentDetailsPayload | null;
  tag_ids?: number[];
}

export function normalizeAppointment(payload: AppointmentApiResponse): Appointment {
  return {
    id: payload.id,
    patient_id: payload.patient_id,
    room_id: payload.room_id,
    start_time: payload.start_time,
    end_time: payload.end_time,
    appointment_type: payload.appointment_type,
    status: payload.status,
    doctor_id: payload.doctor_id,
    doctor_name: payload.doctor_name,
    details: payload.details,
    created_at: payload.created_at,
    updated_at: payload.updated_at,
    patient_name: payload.patient_name,
    tags: payload.tags,
  };
}

export function normalizeAppointmentList(payload: AppointmentListApiResponse): AppointmentListResponse {
  return {
    appointments: payload.appointments.map(normalizeAppointment),
    total: payload.total,
    page: payload.page,
    per_page: payload.per_page,
  };
}

export function getAppointmentDurationMinutes(appointment: Pick<Appointment, 'start_time' | 'end_time'>): number {
  const duration = Date.parse(appointment.end_time) - Date.parse(appointment.start_time);
  return Math.max(0, Math.round(duration / 60_000));
}

export function getAppointmentNotes(appointment: Pick<Appointment, 'details'>): string | null {
  return appointment.details?.notes ?? null;
}

export function getAppointmentChiefComplaint(appointment: Pick<Appointment, 'details'>): string | null {
  return appointment.details?.chief_complaint ?? null;
}
